import React, { useState, useMemo } from "react";
import Select from "./../Select";
import { Empty, Select as SelectAnt } from "antd";
import { SelectProps } from "antd/lib/select";
import { debounce, isEmpty, find, get } from "lodash";
import compact from "lodash/compact";
import metaService from "./services/metaService";
import useSWR from "swr";
import { useTranslation } from "react-i18next";

export interface IBrand {
  id: string;
  name: string;
  created_at: number;
  updated_at: number;
  created_by: string;
  updated_by: string;
  code: string;
  tiki_brand_id: string;
  status: string;
  description: string;
}

interface IProps extends SelectProps<any> {
  kind:
    | "meta_channel"
    | "meta_partner"
    | "meta_ads_type"
    | "meta_device"
    | "meta_country_region"
    | "meta_spend_strategy";
}

const MetaSelect = (props: IProps) => {
  const { value, onChange, kind, ...rest } = props;
  const [searchText, setSearchText] = useState("");

  const { data, error } = useSWR(
    kind ? [`meta_${kind}_select`] : null,
    () => {
      if (kind) {
        return metaService.getMetaData({
          kind,
          limit: 9999,
          offset: 0,
          ...{ sort_by: kind === "meta_country_region" ? "id" : undefined },
        });
      }
    },
    {
      shouldRetryOnError: false,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      refreshWhenOffline: false,
      revalidateOnReconnect: false,
    }
  );

  const items = useMemo(() => data || [], [data]);
  const loading = kind && data === undefined && !error;

  const val = useMemo(() => get(value, "code") || get(value, "name"), [value]);

  const filteredItems = useMemo(
    () =>
      compact([
        ...(items || []),
        items.length === 0 ? value : undefined,
      ]).filter(
        (s) =>
          !searchText || s.name.toLowerCase().includes(searchText.toLowerCase())
      ),
    [items, searchText, value]
  );

  const debouncedSetSearchText = useMemo(
    () => debounce(setSearchText, 500),
    [setSearchText]
  );

  const handleOnChange = (val) => {
    const item = find(filteredItems, (i) => i.code == val);
    if (onChange && item) {
      onChange(item, { value: item });
    }
  };

  const { t } = useTranslation();

  return (
    <Select
      style={{ display: "flex", flex: 1 }}
      placeholder={t("get_link.select_option")}
      loading={loading}
      data={filteredItems}
      onSearch={debouncedSetSearchText}
      notFoundContent={loading ? null : <Empty />}
      getDisplayText={(val: any) => val.name}
      getValue={(val: any) => val.code}
      showSearch
      filterOption={false}
      value={val}
      onChange={handleOnChange}
      {...rest}
    />
  );
};

export default MetaSelect;
